import React, { useEffect, useRef, useState } from "react";
import Styles from "../styles.module.scss";
import { useTranslation } from "react-i18next";

const CODE_LENGTH = 6;

const getErrorSource = (formErr) => {
    return (
        formErr?.response?.data?.errors ||
        formErr?.data?.errors ||
        formErr?.errors ||
        formErr ||
        {}
    );
};

const getFieldError = (formErr, fieldName) => {
    const errors = getErrorSource(formErr);
    const fieldError = errors?.[fieldName];

    if (!fieldError) return "";

    if (Array.isArray(fieldError)) {
        return fieldError[0] || "";
    }

    return String(fieldError);
};

const SetupStep = ({
                       setupData,
                       onBack,
                       onVerify,
                       isLoading = false,
                       formErr = null,
                   }) => {
    const { t } = useTranslation();

    const inputsRef = useRef([]);

    const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
    const [isCopied, setIsCopied] = useState(false);
    const [isCodeErrorHidden, setIsCodeErrorHidden] = useState(false);

    const verificationCode = code.join("");
    const codeError = getFieldError(formErr, "code");
    const isCodeErrorVisible = !!codeError && !isCodeErrorHidden && !isLoading;
    const isVerifyDisabled = verificationCode.length !== CODE_LENGTH || isLoading;

    const hideCodeError = () => {
        if (!isCodeErrorHidden) {
            setIsCodeErrorHidden(true);
        }
    };

    const codeChangeHandler = (event, index) => {
        const value = event.target.value.replace(/\D/g, "").slice(0, 1);

        setCode((prevState) => {
            const nextState = [...prevState];
            nextState[index] = value;

            return nextState;
        });

        hideCodeError();

        if (value && index < CODE_LENGTH - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const codeKeyDownHandler = (event, index) => {
        if (event.key === "Backspace") {
            hideCodeError();

            if (!code[index] && index > 0) {
                inputsRef.current[index - 1]?.focus();
            }
        }
    };

    const codePasteHandler = (event) => {
        event.preventDefault();

        const pastedValue = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, CODE_LENGTH);

        if (!pastedValue) return;

        const nextCode = Array(CODE_LENGTH).fill("");

        pastedValue.split("").forEach((digit, index) => {
            nextCode[index] = digit;
        });

        setCode(nextCode);
        hideCodeError();

        const nextFocusIndex = Math.min(pastedValue.length, CODE_LENGTH - 1);

        inputsRef.current[nextFocusIndex]?.focus();
    };

    const copySecretHandler = async () => {
        if (!setupData?.secret) return;

        try {
            await navigator.clipboard.writeText(setupData.secret);

            setIsCopied(true);

            setTimeout(() => {
                setIsCopied(false);
            }, 1600);
        } catch (error) {
            console.error("Copy secret failed:", error);
        }
    };

    const verifyHandler = () => {
        if (isVerifyDisabled) return;

        setIsCodeErrorHidden(false);
        onVerify(verificationCode);
    };

    useEffect(() => {
        setCode(Array(CODE_LENGTH).fill(""));
        setIsCodeErrorHidden(false);
    }, [setupData]);

    useEffect(() => {
        if (formErr) {
            setIsCodeErrorHidden(false);
        }
    }, [formErr]);

    return (
        <div className={Styles.setupStep}>
            <div className={Styles.setupProviderCard}>
                <div className={Styles.setupProviderIcon}>
                    {/* Google Authenticator icon here */}
                </div>

                <div className={Styles.setupProviderName}>
                    {setupData?.provider_label || "Google Authenticator"}
                </div>
            </div>

            <div className={Styles.setupGrid}>
                <div className={Styles.qrColumn}>
                    <div className={Styles.fieldSmallLabel}>
                        {t("Отсканируйте в приложении-аутентификаторе")}
                    </div>

                    <div className={Styles.qrBox}>
                        {setupData?.qr ? (
                            <img
                                src={setupData.qr}
                                alt="2FA QR code"
                                className={Styles.qrImage}
                            />
                        ) : (
                            <div className={Styles.qrEmpty}>
                                {t("QR-код не найден")}
                            </div>
                        )}
                    </div>
                </div>

                <div className={Styles.verifyColumn}>
                    <div className={Styles.formItem}>
                        <label className={Styles.fieldSmallLabel}>
                            {t("Ключ ручной настройки")}
                        </label>

                        <div className={Styles.secretField}>
                            <div className={Styles.secretValue}>
                                {setupData?.secret || "—"}
                            </div>

                            <button
                                type="button"
                                className={`${Styles.copySecretBtn} ${isCopied ? Styles.copySecretBtnCopied : ""}`}
                                onClick={copySecretHandler}
                                disabled={!setupData?.secret || isLoading}
                                aria-label={t("Скопировать ключ")}
                            >
                                <span />
                                <span />
                            </button>
                        </div>
                    </div>

                    <div className={Styles.formItem}>
                        <label className={Styles.fieldSmallLabel}>
                            {t("Код подтверждения")}
                        </label>

                        <div className={Styles.codeInputs}>
                            {code.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(element) => {
                                        inputsRef.current[index] = element;
                                    }}
                                    type="text"
                                    value={digit}
                                    onChange={(event) => codeChangeHandler(event, index)}
                                    onKeyDown={(event) => codeKeyDownHandler(event, index)}
                                    onPaste={codePasteHandler}
                                    className={`
                                        ${Styles.codeInput}
                                        ${isCodeErrorVisible ? Styles.codeInputError : ""}
                                    `}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={1}
                                    disabled={isLoading}
                                />
                            ))}
                        </div>

                        {isCodeErrorVisible ? (
                            <div className={Styles.codeError}>
                                {t(codeError)}
                            </div>
                        ) : (
                            <div className={Styles.codeHint}>
                                {t("Введите код, который сейчас отображается в приложении.")}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={Styles.actionsRow}>
                <button
                    type="button"
                    className={Styles.cancelBtn}
                    onClick={onBack}
                    disabled={isLoading}
                >
                    {t("Назад")}
                </button>

                <button
                    type="button"
                    className={Styles.continueBtn}
                    onClick={verifyHandler}
                    disabled={isVerifyDisabled}
                >
                    {isLoading ? t("Проверка") : t("Проверить и включить")}
                </button>
            </div>

            <div className={Styles.bottomNote}>
                {t("Коды на основе времени обновляются каждые 30 секунд.")}
            </div>
        </div>
    );
};

export default SetupStep;